import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import IVendorModel from "../../../models/IVendorModel";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import IContact from "../../../models/IContact";
import IOrder from "../../../models/IOrder";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnGetOrdersDTO from "../../../models/DTO/IOnGetOrdersDTO";
import IGetOrdersDTO from "../../../models/DTO/IGetOrdersDTO";
import IOnGetVendorDTO from "../../../models/DTO/IOnGetVendorDTO";
import IGetVendorDTO from "../../../models/DTO/IGetVendorDTO";
import { useNavigate, useMatch } from "react-router-dom";
import * as _ from "lodash-es";
import OwLabel from "../OwLabel/OwLabel";
import PaginationBar from "../PaginationBar/PaginationBar";
import UserService from "../../../services/UserService";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { OwFnInput } from "../OwFnInput/OwFnInput";
import OrderEntryService from "../../../services/OrderEntryService";
import OrderStatesEnum from "../../../models/OrderStatesEnum";
import OwButton from "../OwButton/OwButton";
import { LoadingResults } from "../LoadingResults/LoadingResults";
import IShipment_Group from "../../../models/IShipment_Group";
import IOnGetShipmentGroupsDTO from "../../../models/DTO/IOnGetShipmentGroupsDTO";
import IGetShipmentGroupsDTO from "../../../models/DTO/IGetShipmentGroupsDTO";
import AddressDisplay from "../AddressDisplay/AddressDisplay";
import OwGoogleStaticMap from "../OwGoogleStaticMap/OwGoogleStaticMap";
import IGetContactsDTO from "../../../models/DTO/IGetContactsDTO";
import IOnGetContactsDTO from "../../../models/DTO/IOnGetContactsDTO";
import { OwIconButton } from "../OwIconButton/OwIconButton";
import OwSelectOption from "../OwSelectOption/OwSelectOption";
import { NumericFormat } from "react-number-format";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { NotesViewer } from "./NotesViewer/NotesViewer";
import "./AccountDetailView.scss";

type Props = {};

export const AccountDetailView: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const match = useMatch("/account/:guid/detail");
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const history = useNavigate();

    const guid = match?.params.guid || "";

    const getStatus = () => {
        const orderSteps = OrderEntryService.getOrderSteps(supplierModel, OrderStatesEnum.PRELIMINARY);
        const statusItems = orderSteps.map((stepItem) => {
            return { name: stepItem.name, id: stepItem.order_state_id };
        });

        return statusItems;
    };

    const [isLoading, setIsLoading] = useState(true);
    const [openOrders, setOpenOrders] = useState<IOrder[]>([]);
    const [openOrdersLimit, setOpenOrdersLimit] = useState(25);
    const [openOrdersOffset, setOpenOrdersOffset] = useState(0);
    const [totalOpenOrders, setTotalOpenOrders] = useState(0);
    const [vendorModel, setVendorModel] = useState<IVendorModel>();
    const [nameFilter, setNameFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [poNoFilter, setPoNoFilter] = useState("");
    const [statuses, setStatuses] = useState<{ name: string; id: number }[]>(getStatus());
    const [resetSearch, setResetSearch] = useState(false);

    // Shipments State
    const [shipmentGroups, setShipmentGroups] = useState<IShipment_Group[]>([]);
    const [totalShipmentGroups, setTotalShipmentGroups] = useState(0);
    const [shipmentGroupLimit, setShipmentGroupLimit] = useState(10);
    const [shipmentGroupOffset, setShipmentGroupOffset] = useState(0);
    const [isShipmentGroupsLoading, setIsShipmentGroupsLoading] = useState(true);
    const [shipmentOrderFilter, setShipmentOrderFilter] = useState("");
    const [resetShipmentSearch, setResetShipmentSearch] = useState(false);

    // Contacts State
    const [contacts, setContacts] = useState<IContact[]>([]);
    const [totalContacts, setTotalContacts] = useState(0);

    useEffect(() => {
        socket.on(EndpointsEnum.OrderOnGetOrders, onGetOrders);
        socket.on(EndpointsEnum.VendorsOnGetVendor, onGetVendor);
        socket.on(EndpointsEnum.ShipmentOnGetShipmentGroups, onGetShipmentGroups);
        socket.on(EndpointsEnum.ContactsOnGetContacts, onGetContacts);

        getVendor();

        return () => {
            socket.removeListener(EndpointsEnum.OrderOnGetOrders, onGetOrders);
            socket.removeListener(EndpointsEnum.VendorsOnGetVendor, onGetVendor);
            socket.removeListener(EndpointsEnum.ShipmentOnGetShipmentGroups, onGetShipmentGroups);
            socket.removeListener(EndpointsEnum.ContactsOnGetContacts, onGetContacts);
        };
    }, []);

    useEffect(() => {
        getOrders();
        getShipmentGroups();
        getContacts();
    }, [vendorModel]);

    useEffect(() => {
        getOrders();
    }, [openOrdersOffset, openOrdersLimit]);

    useEffect(() => {
        getShipmentGroups();
    }, [shipmentGroupLimit, shipmentGroupOffset]);

    useEffect(() => {
        getOrders();
    }, [resetSearch]);

    useEffect(() => {
        getShipmentGroups();
    }, [resetShipmentSearch]);

    const getQueryString = (): string => {
        const params = [
            `vendor=${vendorModel!.guid}`,
            `vendor_name=${vendorModel!.name}`,
            `filter_shipped=true`,
            `filter_delivered=true`,
            `filter_picked_up=true`,
            `is_quote=0`,
            `is_template=0`,
            `order_by=updated_at`,
            `order_dir=desc`,
        ];

        if (statusFilter.length) {
            params.push(`order_state=${statusFilter}`);
        }

        if (nameFilter.length) {
            params.push(`order=${nameFilter}`);
        }

        if (poNoFilter.length) {
            params.push(`po_no=${poNoFilter}`);
        }

        return "?" + params.join("&");
    };

    const getShipmentsQueryString = () => {
        const params = [`vendor=${guid}`];

        if (shipmentOrderFilter.length) {
            params.push(`order=${shipmentOrderFilter}`);
        }

        return "?" + params.join("&");
    };

    const getShipmentGroups = () => {
        const queryString = getShipmentsQueryString();

        const payload: IGetShipmentGroupsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            limit: shipmentGroupLimit,
            offset: shipmentGroupOffset,
            queryString,
        };

        socket.emit(EndpointsEnum.ShipmentGetShipmentGroups, payload);

        setIsShipmentGroupsLoading(true);
    };

    const onGetShipmentGroups = (data: IOnGetShipmentGroupsDTO) => {
        setShipmentGroups(data.shipmentGroups);
        setTotalShipmentGroups(data.totalShipmentGroups);

        if (shipmentOrderFilter.length) {
            setShipmentGroupLimit(data.totalShipmentGroups);
            setShipmentGroupOffset(0);
        }

        setIsShipmentGroupsLoading(false);
    };

    const resetShipmentsFilter = () => {
        setShipmentOrderFilter("");
        setShipmentGroupOffset(0);
        setShipmentGroupLimit(10);
        setResetShipmentSearch(!resetShipmentSearch);
    };

    const getContacts = () => {
        const payload: IGetContactsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: guid,
        };

        socket.emit(EndpointsEnum.ContactsGetContacts, payload);
    };

    const onGetContacts = (data: IOnGetContactsDTO) => {
        setContacts(data.contacts);
    };

    const getOrders = () => {
        if (vendorModel) {
            const queryString = getQueryString();

            const payload: IGetOrdersDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                limit: openOrdersLimit,
                offset: openOrdersOffset,
                queryString,
                callMeta: {
                    view: "AccountDetailView",
                    fn: "getOrders",
                },
            };

            socket.emit(EndpointsEnum.OrderGetOrders, payload);

            setIsLoading(true);
        }
    };

    const onGetOrders = (data: IOnGetOrdersDTO) => {
        setOpenOrders(data.orders);
        setTotalOpenOrders(data.totalOrders);
        setIsLoading(false);
    };

    const getVendor = () => {
        const payload: IGetVendorDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: guid,
        };

        socket.emit(EndpointsEnum.VendorsGetVendor, payload);
    };

    const onGetVendor = (data: IOnGetVendorDTO) => {
        setVendorModel(data.vendor);

        document.title = `Orderwerks >> ${data.vendor.name} >> Detail`;
    };

    const onOpenOrdersPaginationChanged = (offset: number, limit: number) => {
        setOpenOrdersLimit(limit);
        setOpenOrdersOffset(offset);
    };

    const onShipmentPaginationChanged = (offset: number, limit: number) => {
        setShipmentGroupLimit(limit);
        setShipmentGroupOffset(offset);
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const name = e.target.name;
        const value = e.target.value;

        switch (name) {
            case "nameFilter":
                setNameFilter(value);
                break;

            case "statusFilter":
                setStatusFilter(value);
                break;

            case "poNoFilter":
                setPoNoFilter(value);
                break;

            case "shipmentOrderFilter":
                setShipmentOrderFilter(value);
                break;
        }
    };

    const doSearch = () => {
        getOrders();
    };

    const resetSearchFn = () => {
        setNameFilter("");
        setStatusFilter("");
        setPoNoFilter("");
        setOpenOrdersOffset(0);
        setOpenOrdersLimit(25);
        setResetSearch(!resetSearch);
    };

    const getViewOrdersQueryString = (): string => {
        const params = [`vendor=${vendorModel!.guid}`, `vendor_name=${vendorModel!.name}`];

        if (nameFilter.length) {
            params.push(`order=${nameFilter}`);
        }

        if (poNoFilter.length) {
            params.push(`po_no=${poNoFilter}`);
        }

        if (statusFilter.length) {
            params.push(`order_state=${statusFilter}`);
        }

        params.push(`filter_shipped=true`, `filter_delivered=true`, `filter_picked_up=true`);

        return "?" + params.join("&");
    };

    const viewOrders = () => {
        const queryString = getViewOrdersQueryString();

        history(`/orders${queryString}`);
    };

    const viewContacts = () => {
        history(`/account/${guid}/contacts`);
    };

    const viewShipments = () => {
        history(`/shipments?vendor=${guid}&offset=0&limit=5`);
    };

    const availableCredit = (vendorModel?.credit_limit || 0) - (vendorModel?.open_balance || 0);

    return (
        <div className={"AccountDetailView"}>
            <div className="row">
                <div className="col-12">
                    {vendorModel ? (
                        <div className={"d-flex justify-content-between"}>
                            <OwTitle title={vendorModel?.name} disableTranslation={true} />
                            {supplierModel.has_vendor_credit_limits && vendorModel ? (
                                <div
                                    className={"mt-2 p-1 border rounded"}
                                    style={{
                                        minWidth: "280px",
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gridTemplateRows: "auto auto auto auto auto",
                                        gap: "0.5rem",
                                        gridTemplateAreas: `
                                            "title title"
                                            "hr1 hr1"
                                            "amount1 amount2"
                                            "hr2 hr2"
                                            "subtitle1 subtitle2"
                                        `,
                                    }}>
                                    <div style={{ gridArea: "title" }}>
                                        <OwTitle
                                            title={"Credit"}
                                            tag={"h5"}
                                            disableTranslation={true}
                                            className={"text-center mb-0"}
                                            defaultMargins={false}
                                        />
                                    </div>

                                    <hr className={"my-0"} style={{ gridArea: "hr1", width: "100%" }} />

                                    <div
                                        style={{ gridArea: "amount1" }}
                                        className={"text-center d-flex flex-column justify-content-center"}>
                                        <div className={"fs-5 fw-bold text-primary"}>
                                            {(vendorModel.credit_limit || 0) > 0 ? (
                                                <NumericFormat
                                                    value={vendorModel.credit_limit || 0}
                                                    fixedDecimalScale={true}
                                                    thousandSeparator={true}
                                                    displayType={"text"}
                                                    decimalScale={2}
                                                    prefix={"$"}
                                                />
                                            ) : (
                                                "N/A"
                                            )}
                                        </div>
                                    </div>

                                    <div
                                        style={{ gridArea: "amount2" }}
                                        className={"text-center d-flex flex-column justify-content-center border-start ps-2"}>
                                        <div className={"fs-5 fw-bold text-success"}>
                                            {(vendorModel.credit_limit || 0) > 0 ? (
                                                <NumericFormat
                                                    value={availableCredit || 0}
                                                    fixedDecimalScale={true}
                                                    thousandSeparator={true}
                                                    displayType={"text"}
                                                    decimalScale={2}
                                                    prefix={"$"}
                                                />
                                            ) : (
                                                "N/A"
                                            )}
                                        </div>
                                    </div>

                                    <hr className={"my-0"} style={{ gridArea: "hr2", width: "100%" }} />

                                    <div style={{ gridArea: "subtitle1" }} className={"text-center"}>
                                        <OwTitle
                                            title={"Limit"}
                                            tag={"h6"}
                                            disableTranslation={true}
                                            defaultMargins={false}
                                            className={"text-muted mb-0 mt-0"}
                                        />
                                    </div>

                                    <div style={{ gridArea: "subtitle2" }} className={"text-center"}>
                                        <OwTitle
                                            title={"Available"}
                                            tag={"h6"}
                                            disableTranslation={true}
                                            defaultMargins={false}
                                            className={"text-muted mb-0 mt-0"}
                                        />
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ) : (
                        <OwTitle title={"loading"} />
                    )}
                </div>
            </div>
            <AccountsMainView className="row">
                <ColumnContainer className="d-flex flex-column col-6">
                    <div className="d-flex align-items-center">
                        <OwTitle title={"orders"} tag={"h3"} />
                        <OwIconButton iconClass={"fa-duotone fa-up-right-from-square"} iconColorClass={"ms-2 btn-link"} fn={viewOrders} />
                    </div>
                    <div className={"d-flex mb-2"}>
                        <div>
                            <OwLabel title={"name"} />
                            <OwFnInput fn={handleChange} name={"nameFilter"} value={nameFilter} />
                        </div>
                        <div className={"ms-2"}>
                            <OwLabel title={"status"} />
                            <OwSelectOption
                                translateTitle={true}
                                name={"statusFilter"}
                                options={statuses}
                                titleProp={"name"}
                                valueProp={"id"}
                                fn={handleChange}
                                selectedOption={statusFilter}
                                hasSelectedOptionProp={false}
                            />
                        </div>
                        <div className={"ms-2"}>
                            <OwLabel title={"po_no"} />
                            <OwFnInput fn={handleChange} name={"poNoFilter"} value={poNoFilter} />
                        </div>
                        <div className={"ms-2 d-flex align-items-end"}>
                            <OwButton title={"search"} fn={doSearch} active={isLoading} disableOnActive={true} />
                            <OwButton title={"reset"} fn={resetSearchFn} className={"btn-danger ms-2"} disabled={isLoading} />
                        </div>
                        <div className={"ms-auto d-flex align-items-end"}>
                            <OwLabel className={"me-2"} title={"results"} postText={":"} />
                            {totalOpenOrders}
                        </div>
                    </div>
                    <div className={"d-flex"}>
                        <div className="w-20">
                            <OwLabel isBold={true} title={"name"} />
                        </div>
                        <div className="w-20">
                            <OwLabel isBold={true} title={"status"} />
                        </div>
                        <div className="w-20">
                            <OwLabel isBold={true} title={"po_no"} />
                        </div>
                        <div className="w-20">
                            <OwLabel isBold={true} title={"created_at"} />
                        </div>
                        <div className="w-20">
                            <OwLabel isBold={true} title={"updated_at"} />
                        </div>
                    </div>
                    <OpenOrdersContainer>
                        {isLoading ? (
                            <div className={"d-flex h-100 justify-content-center align-items-center"}>
                                <LoadingResults />
                            </div>
                        ) : (
                            openOrders.map((order) => {
                                return (
                                    <OpenOrdersRow key={order.guid}>
                                        <div className={"w-20"}>
                                            <Link to={`/orders/${order.guid}/entry`}>{order.name}</Link>
                                        </div>
                                        <div className={"w-20"}>{_.startCase(order.Order_State?.name.toLowerCase())}</div>
                                        <div className={"w-20"}>{order.po_no}</div>
                                        <div className={"w-20"}>{UserService.parseDate(order.created_at)}</div>
                                        <div className={"w-20"}>{UserService.parseDate(order.updated_at)}</div>
                                    </OpenOrdersRow>
                                );
                            })
                        )}
                    </OpenOrdersContainer>
                    <div className={"w-100 d-flex flex-column"}>
                        <PaginationBar
                            isActive={isLoading}
                            limitCss={"d-inline-flex"}
                            offset={openOrdersOffset}
                            limit={openOrdersLimit}
                            totalRecords={totalOpenOrders}
                            onPaginationChanged={onOpenOrdersPaginationChanged}
                        />
                    </div>
                </ColumnContainer>

                <ColumnContainer className="d-flex flex-column col-6">
                    <div className={"d-flex align-items-end"}>
                        <div className={"d-flex align-items-center"}>
                            <OwTitle className={"d-flex"} title={"shipments"} tag={"h3"} />
                            <OwIconButton
                                iconClass={"fa-duotone fa-up-right-from-square"}
                                iconColorClass={"ms-2 btn-link"}
                                fn={viewShipments}
                            />
                        </div>
                    </div>

                    <div className={"d-flex mb-2"}>
                        <div>
                            <OwLabel title={"order"} />
                            <OwFnInput fn={handleChange} name={"shipmentOrderFilter"} value={shipmentOrderFilter} />
                        </div>
                        <div className={"ms-2 d-flex align-items-end"}>
                            <OwButton title={"search"} fn={getShipmentGroups} />
                            <OwButton className={"ms-2 btn-danger"} title={"reset"} fn={resetShipmentsFilter} />
                        </div>
                        <div className={"d-flex ms-auto"}>
                            <OwLabel title={"results"} postText={":"} className={"me-2"} />
                            {totalShipmentGroups}
                        </div>
                    </div>

                    <div className={"d-flex"}>
                        <div className="w-20">
                            <OwLabel isBold={true} title={"order"} />
                        </div>
                        <div className="w-50">
                            <OwLabel isBold={true} title={"ship_to"} />
                        </div>
                        <div className="w-20">
                            <OwLabel isBold={true} title={"created_at"} />
                        </div>
                    </div>

                    <ShipmentsContainer>
                        {shipmentGroups.map((shipmentGroup) => {
                            return (
                                <ShipmentsRow key={shipmentGroup.guid} className={"d-flex"}>
                                    <div className={"w-20"}>
                                        {shipmentGroup.Orders?.map((order) => {
                                            return <div key={order.guid}>{order.name}</div>;
                                        })}
                                    </div>
                                    <div className={"w-50 d-flex"}>
                                        {shipmentGroup.Ship_To ? (
                                            <OwGoogleStaticMap address={shipmentGroup.Ship_To} size={"100x100"} className={"me-2"} />
                                        ) : null}
                                        <AddressDisplay address={shipmentGroup.Ship_To} />
                                    </div>
                                    <div className={"w-20"}>{UserService.parseDate(shipmentGroup.created_at)}</div>
                                </ShipmentsRow>
                            );
                        })}
                    </ShipmentsContainer>
                    <div>
                        <PaginationBar
                            isActive={isShipmentGroupsLoading}
                            limitCss={"d-inline-flex"}
                            offset={shipmentGroupOffset}
                            limit={shipmentGroupLimit}
                            totalRecords={totalShipmentGroups}
                            onPaginationChanged={onShipmentPaginationChanged}
                        />
                    </div>
                    <div id="contacts-container">
                        <Tabs defaultIndex={0}>
                            <TabList>
                                <Tab>Contacts</Tab>
                                <Tab>Notes</Tab>
                            </TabList>

                            <TabPanel>
                                <div className="m-3">
                                    <div className={"d-flex align-items-center"}>
                                        <OwTitle title={"contacts"} tag={"h3"} />
                                        <OwIconButton
                                            iconClass={"fa-duotone fa-up-right-from-square"}
                                            iconColorClass={"ms-2 btn-link"}
                                            fn={viewContacts}
                                        />
                                    </div>
                                    <div className={"d-flex flex-column"}>
                                        <div className={"d-flex"}>
                                            <div className="w-40">
                                                <OwLabel title={"name"} isBold={true} />
                                            </div>
                                            <div className="w-30">
                                                <OwLabel title={"phone"} isBold={true} />
                                            </div>
                                            <div className="w-30">
                                                <OwLabel title={"email"} isBold={true} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className={"contacts-container"}>
                                        {contacts.map((contact) => {
                                            return (
                                                <div key={contact.guid} className={"contacts-row"}>
                                                    <div className={"w-40"}>{contact.name}</div>
                                                    <div className={"w-30"}>{contact.phone}</div>
                                                    <div className={"w-30"}>{contact.email}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </TabPanel>

                            <TabPanel>{vendorModel?.guid && <NotesViewer vendorModel={vendorModel} vendorContacts={contacts} />}</TabPanel>
                        </Tabs>
                    </div>
                </ColumnContainer>
            </AccountsMainView>
        </div>
    );
};

const AccountsMainView = styled.div`
    flex-grow: 1;
    height: 100%;
    max-height: 100%;
    overflow: hidden;
`;

const ColumnContainer = styled.div`
    height: 100%;
    flex-grow: 1;
    max-height: 100%;
`;

const OpenOrdersContainer = styled.div`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    overflow-y: scroll;
    height: 100%;
`;

const OpenOrdersRow = styled.div`
    display: flex;
    padding: 0.5rem;

    :nth-of-type(even) {
        background-color: var(--secondary-bg-color);
    }
`;

const ShipmentsContainer = styled.div`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    height: 70%;
    overflow-y: scroll;
`;

const ShipmentsRow = styled.div`
    display: flex;
    padding: 0.5rem;

    :nth-of-type(even) {
        background-color: var(--secondary-bg-color);
    }
`;
